import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { toast } from 'react-toastify';

export default function CampaignDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [distStatus, setDistStatus] = useState(null); // 'processing' | 'completed' | 'failed' | null
  const [distProgress, setDistProgress] = useState(0);
  const pollRef = useRef(null);

  const fetchData = async () => {
    try {
      const { data } = await api.get(`/admin/campaigns/${id}`);
      setCampaign(data.campaign);
      setAvailableUsers(data.availableUsers);
      // If distribution is already processing, start polling
      if (data.campaign.distributionStatus === 'processing') {
        setDistStatus('processing');
        setDistProgress(data.campaign.distributionProgress || 0);
        startPolling();
      } else {
        setDistStatus(data.campaign.distributionStatus === 'idle' ? null : data.campaign.distributionStatus);
        setDistProgress(data.campaign.distributionProgress || 0);
      }
    } catch { toast.error('Failed to load campaign'); navigate('/admin/campaigns'); }
    finally { setLoading(false); }
  };

  const startPolling = useCallback(() => {
    if (pollRef.current) return; // already polling
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/admin/campaigns/${id}/distribution-status`);
        setDistProgress(data.progress);
        setDistStatus(data.status);
        if (data.status === 'completed') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          toast.success(`Distribution complete! ${(data.totalAssigned || 0).toLocaleString()} records assigned.`);
          fetchData(); // refresh campaign data
        } else if (data.status === 'failed') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          toast.error('Distribution failed. Please try again.');
        }
      } catch {
        // ignore polling errors silently
      }
    }, 3000);
  }, [id]);

  useEffect(() => { fetchData(); return () => { if (pollRef.current) clearInterval(pollRef.current); }; }, [id]);

  const handleAssignUsers = async () => {
    if (!selectedUsers.length) return toast.warning('Select at least one user');
    try {
      await api.post(`/admin/campaigns/${id}/assign-users`, { userIds: selectedUsers });
      toast.success('Users assigned');
      setSelectedUsers([]);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.message || 'Error'); }
  };

  const handleDistribute = async () => {
    try {
      setDistStatus('processing');
      setDistProgress(0);
      const { data } = await api.post(`/admin/campaigns/${id}/distribute`, { method: 'equal' });
      toast.info(data.message || 'Distribution started...');
      startPolling();
    } catch (err) {
      setDistStatus(null);
      const msg = err.response?.data?.message || 'Distribution failed';
      if (err.response?.status === 409) {
        toast.warning(msg);
        startPolling(); // already processing, start polling
        setDistStatus('processing');
      } else {
        toast.error(msg);
      }
    }
  };

  if (loading) return <div className="loading-spinner"><div className="spinner" /></div>;
  if (!campaign) return null;

  const totalSent = campaign.totalSent || 0;
  const totalFailed = campaign.totalFailed || 0;
  const totalAssigned = campaign.totalAssigned || 0;
  const totalProgress = totalAssigned
    ? ((totalSent / totalAssigned) * 100).toFixed(1)
    : 0;

  return (
    <div>
      <div className="page-header">
        <div>
          <button className="btn btn-secondary btn-sm" onClick={() => navigate('/admin/campaigns')} style={{ marginBottom: 8 }}>
            ← Back to Campaigns
          </button>
          <h1 className="page-title">{campaign.name}</h1>
          <p className="page-subtitle">
            <span className={`badge badge-${campaign.type}`}>{(campaign.type || '').replace('_', ' ')}</span>
            {' '}
            <span className={`badge badge-${campaign.status}`}>{campaign.status}</span>
          </p>
        </div>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-card-icon green">✓</div>
          <div className="stat-card-value">{totalSent.toLocaleString()}</div>
          <div className="stat-card-label">Sent</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-icon red">✕</div>
          <div className="stat-card-value">{totalFailed.toLocaleString()}</div>
          <div className="stat-card-label">Failed</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-icon amber">◷</div>
          <div className="stat-card-value">{(totalAssigned - totalSent - totalFailed).toLocaleString()}</div>
          <div className="stat-card-label">Pending</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-icon purple">◎</div>
          <div className="stat-card-value">{campaign.assignedUsers?.length || 0}</div>
          <div className="stat-card-label">Assigned Users</div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
          <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>Progress</span>
          <span style={{ fontSize: 13, fontWeight: 700 }}>{totalProgress}%</span>
        </div>
        <div className="progress-bar">
          <div className="progress-fill" style={{ width: `${totalProgress}%` }} />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>

        {/* Distribution Status Banner */}
        {distStatus === 'processing' && (
          <div style={{ gridColumn: '1 / -1', background: 'linear-gradient(135deg, rgba(126,165,46,0.1), rgba(126,165,46,0.05))',
            border: '1px solid rgba(126,165,46,0.3)', borderRadius: 10, padding: '14px 20px',
            display: 'flex', alignItems: 'center', gap: 14 }}>
            <span className="spinner" style={{ width: 22, height: 22, borderColor: 'rgba(126,165,46,0.3)', borderTopColor: '#7ea52e' }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: '#7ea52e', marginBottom: 4 }}>
                Distributing data... {distProgress}%
              </div>
              <div style={{ background: 'rgba(126,165,46,0.15)', borderRadius: 6, height: 8, overflow: 'hidden' }}>
                <div style={{ width: `${distProgress}%`, height: '100%', background: '#7ea52e', borderRadius: 6,
                  transition: 'width 0.6s ease' }} />
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                Large datasets may take several minutes. You can leave this page — distribution continues in background.
              </div>
            </div>
          </div>
        )}
        {distStatus === 'failed' && (
          <div style={{ gridColumn: '1 / -1', background: 'rgba(220,53,69,0.08)',
            border: '1px solid rgba(220,53,69,0.3)', borderRadius: 10, padding: '14px 20px',
            display: 'flex', alignItems: 'center', gap: 14 }}>
            <span style={{ fontSize: 20 }}>⚠</span>
            <div>
              <div style={{ fontWeight: 700, fontSize: 14, color: '#dc3545' }}>Distribution failed</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Please try distributing again.</div>
            </div>
            <button className="btn btn-sm btn-primary" onClick={handleDistribute} style={{ marginLeft: 'auto' }}>
              Retry
            </button>
          </div>
        )}
        {/* Assigned Users */}
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Assigned Users</h3>
            {distStatus === 'processing' ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 220 }}>
                <div style={{ flex: 1, background: 'var(--bg-secondary)', borderRadius: 6, height: 22, overflow: 'hidden', position: 'relative' }}>
                  <div style={{
                    width: `${distProgress}%`, height: '100%',
                    background: 'linear-gradient(90deg, #7ea52e, #b7d083)',
                    borderRadius: 6, transition: 'width 0.6s ease',
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#fff', textShadow: '0 1px 2px rgba(0,0,0,.3)' }}>
                      {distProgress}%
                    </span>
                  </div>
                </div>
                <span className="spinner" style={{ width: 18, height: 18 }} />
              </div>
            ) : (
              <button className="btn btn-sm btn-primary" onClick={handleDistribute}
                disabled={distStatus === 'processing'}>
                Distribute Data
              </button>
            )}
          </div>
          {campaign.assignedUsers?.length ? (
            <div className="table-container">
              <table className="data-table">
                <thead><tr><th>Username</th><th>Email</th><th>Status</th></tr></thead>
                <tbody>
                  {campaign.assignedUsers.map(u => (
                    <tr key={u._id}>
                      <td style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{u.username}</td>
                      <td>{u.email}</td>
                      <td><span className={`badge badge-${u.status}`}>{u.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="empty-state"><p>No users assigned yet</p></div>
          )}
        </div>

        {/* Available Users */}
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Assign New Users</h3>
            <button className="btn btn-sm btn-primary" onClick={handleAssignUsers}
              disabled={!selectedUsers.length}>
              Assign ({selectedUsers.length})
            </button>
          </div>
          {availableUsers.length ? (
            <div style={{ maxHeight: 300, overflowY: 'auto' }}>
              {availableUsers.map(u => (
                <label key={u._id} className="checkbox-item">
                  <input type="checkbox" checked={selectedUsers.includes(u._id)}
                    onChange={e => {
                      if (e.target.checked) setSelectedUsers([...selectedUsers, u._id]);
                      else setSelectedUsers(selectedUsers.filter(id => id !== u._id));
                    }} />
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{u.username}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{u.email}</div>
                  </div>
                </label>
              ))}
            </div>
          ) : (
            <div className="empty-state"><p>All users are already assigned</p></div>
          )}
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Campaign Info</h3>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, fontSize: 14 }}>
          <div>
            <span style={{ color: 'var(--text-muted)', display: 'block' }}>Created By</span>
            <span style={{ fontWeight: 600 }}>{campaign.createdBy?.username}</span>
          </div>
          <div>
            <span style={{ color: 'var(--text-muted)', display: 'block' }}>Fail Count</span>
            <span style={{ fontWeight: 600 }}>{campaign.failCount || 0}</span>
          </div>
          <div>
            <span style={{ color: 'var(--text-muted)', display: 'block' }}>Created</span>
            <span style={{ fontWeight: 600 }}>{new Date(campaign.createdAt).toLocaleDateString()}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
